import { useState, useEffect, useCallback } from "react";
import { Alert, Collapse, IconButton, Box, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { getServiceStatus, getServiceStatusDemo, ServiceStatus } from "../../api/client";

/** Poll interval in milliseconds (2 minutes) */
const POLL_INTERVAL_MS = 2 * 60 * 1000;

/** Check if URL has ?statusDemo=true for visual testing */
const isDemoMode = () =>
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).get("statusDemo") === "true";

/**
 * Global banner that shows when a service provider (Render / Vercel) has an
 * active incident.  The message is AI-generated to be non-technical and
 * reassuring for end-users.
 */
export default function ServiceStatusBanner() {
  const [status, setStatus] = useState<ServiceStatus | null>(null);
  const [dismissed, setDismissed] = useState(false);
  // Track the incident key that was dismissed so we re-show on NEW incidents
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const data = isDemoMode() ? await getServiceStatusDemo() : await getServiceStatus();
      setStatus(data);

      // If a new set of incidents appears that differs from what was dismissed, re-show
      if (data.hasActiveIncidents) {
        const currentKey = data.incidents.map((i) => i.id).sort().join(",");
        if (currentKey !== dismissedKey) {
          setDismissed(false);
        }
      }
    } catch {
      // Swallow — banner should never break the app
    }
  }, [dismissedKey]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const handleDismiss = () => {
    setDismissed(true);
    if (status?.incidents.length) {
      setDismissedKey(status.incidents.map((i) => i.id).sort().join(","));
    }
  };

  const show = status?.hasActiveIncidents && !dismissed;

  return (
    <Collapse in={show}>
      <Alert
        severity="warning"
        icon={<WarningAmberIcon fontSize="inherit" />}
        action={
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={handleDismiss}
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        }
        sx={{
          borderRadius: 0,
          borderBottom: 1,
          borderColor: "warning.light",
          px: { xs: 2, sm: 3, xl: 4 },
          alignItems: "center",
          "& .MuiAlert-message": { width: "100%" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ textAlign: "center" }}>
            {status?.userMessage ||
              "We're currently aware of a temporary disruption with one of our service providers and are actively working to fix it. Your data is safe. Feel free to reach us on WhatsApp if you need any help — thank you for your patience!"}
          </Typography>
        </Box>
      </Alert>
    </Collapse>
  );
}
